import type { TFunction } from 'i18next';
import { FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Link,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useFormatting } from '@ayeldo/frontend-core/hooks/useFormatting';

import { useIdpSession } from '../../hooks/useIdpSession';
import { adminApi } from '../../services/admin-api';
import { APIError, idpApi, type ProfileResponse } from '../../services/idp-api';

function credentialLabel(
  credential: ProfileResponse['credentials'][number],
  t: TFunction<'idp'>
): string {
  if (credential.type === 'google') {
    if (credential.googleEmail !== undefined && credential.googleEmail !== '') {
      return t('dashboard.credentialGoogle', { email: credential.googleEmail });
    }
    return t('dashboard.credentialGoogleLinked');
  }

  return t('dashboard.credentialPassword');
}

export function DashboardPage() {
  const { t } = useTranslation('idp');
  const navigate = useNavigate();
  const { formatDateTime } = useFormatting();
  const { isAuthenticated, identity, isLoading } = useIdpSession();
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/auth/signin', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  useEffect(() => {
    if (identity) {
      setName(identity.name);
    }
  }, [identity]);

  useEffect(() => {
    if (!isAuthenticated) return;
    adminApi
      .listGroups()
      .then(() => {
        setIsPlatformAdmin(true);
      })
      .catch(() => {
        setIsPlatformAdmin(false);
      });
  }, [isAuthenticated]);

  async function handleSave(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setErrorMessage(null);
    setSuccessMessage(null);
    setIsSubmitting(true);

    try {
      const updated = await idpApi.updateProfile({ name: name.trim() });
      setName(updated.name);
      setSuccessMessage(t('dashboard.successMessage'));
    } catch (error) {
      if (error instanceof APIError) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage(t('dashboard.errors.default'));
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  if (isLoading || !identity) {
    return (
      <Stack alignItems="center" sx={{ py: 8 }}>
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <Stack spacing={3}>
      <Card>
        <CardContent>
          <Stack
            spacing={2}
            component="form"
            onSubmit={(event) => {
              void handleSave(event);
            }}
          >
            <Typography variant="h5" component="h1" fontWeight={700}>
              {t('dashboard.title')}
            </Typography>

            {successMessage !== null ? <Alert severity="success">{successMessage}</Alert> : null}
            {errorMessage !== null ? <Alert severity="error">{errorMessage}</Alert> : null}

            <TextField
              label={t('dashboard.emailLabel')}
              value={identity.email}
              fullWidth
              disabled
            />
            <TextField
              label={t('dashboard.nameLabel')}
              value={name}
              onChange={(event) => {
                setName(event.target.value);
              }}
              fullWidth
              required
            />

            <Button type="submit" variant="contained" disabled={isSubmitting || name.trim() === ''}>
              {isSubmitting ? t('dashboard.savingButton') : t('dashboard.saveButton')}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Stack spacing={2}>
            <Typography variant="h6" component="h2" fontWeight={600}>
              {t('dashboard.credentialsTitle')}
            </Typography>
            <List disablePadding>
              {identity.credentials.map((credential) => (
                <ListItem key={`${credential.type}-${credential.createdAt}`} disableGutters>
                  <ListItemText
                    primary={credentialLabel(credential, t)}
                    secondary={t('dashboard.credentialAdded', {
                      date: formatDateTime(credential.createdAt),
                    })}
                  />
                </ListItem>
              ))}
            </List>
            <Box>
              <Link component={RouterLink} to="/account/credentials" underline="hover">
                {t('dashboard.manageCredentials')}
              </Link>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Stack direction="row" spacing={3}>
        <Link component={RouterLink} to="/account/sessions" underline="hover">
          {t('dashboard.manageSessions')}
        </Link>
        <Link component={RouterLink} to="/account/delete" underline="hover" color="error.main">
          {t('dashboard.deleteAccount')}
        </Link>
      </Stack>

      {isPlatformAdmin && (
        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Typography variant="h6" component="h2" fontWeight={600}>
                {t('dashboard.adminTitle')}
              </Typography>
              <Stack direction="row" spacing={3}>
                <Link component={RouterLink} to="/admin/users" underline="hover">
                  {t('dashboard.manageUsers')}
                </Link>
                <Link component={RouterLink} to="/admin/groups" underline="hover">
                  {t('dashboard.manageGroups')}
                </Link>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      )}
    </Stack>
  );
}
